import React, { useState } from "react";

interface Competency {
  id: string;
  staffName: string;
  role: string;
  trainingDate: string;
  competencyDate: string;
  assessor: string;
  nextDue: string;
  notes: string;
}

interface CdCheck {
  id: string;
  date: string;
  checker: string;
  drug: string;
  balanceExpected: string;
  balanceActual: string;
  discrepancy: boolean;
  action: string;
}

const COMP_KEY = "med_competency";
const CHECK_KEY = "cd_checks";

const load = <T,>(key: string): T[] => {
  try {
    return JSON.parse(localStorage.getItem(key) || "[]");
  } catch {
    return [];
  }
};
const saveKey = (key: string, data: any[]) => localStorage.setItem(key, JSON.stringify(data));
const today = () => new Date().toISOString().slice(0, 10);
const daysTo = (d: string) => Math.ceil((new Date(d).getTime() - Date.now()) / 86400000);

export default function MedCompetency({ user }: { user?: any }) {
  const [tab, setTab] = useState<"comp" | "checks" | "breaches">("comp");
  const [comps, setComps] = useState<Competency[]>(() => load(COMP_KEY));
  const [checks, setChecks] = useState<CdCheck[]>(() => load(CHECK_KEY));
  const [compForm, setCompForm] = useState<Partial<Competency>>({ trainingDate: today() });
  const [checkForm, setCheckForm] = useState<Partial<CdCheck>>({ date: today(), checker: user && user.name ? user.name : "" });
  const [message, setMessage] = useState("");

  const say = (m: string) => { setMessage(m); setTimeout(() => setMessage(""), 4000); };

  const saveComp = () => {
    if (!compForm.staffName) { say("Staff member name is required."); return; }
    const rec: Competency = { id: `mc-${Date.now()}`, staffName: compForm.staffName || "", role: compForm.role || "", trainingDate: compForm.trainingDate || "", competencyDate: compForm.competencyDate || "", assessor: compForm.assessor || "", nextDue: compForm.nextDue || "", notes: compForm.notes || "" };
    const next = [rec, ...comps];
    setComps(next); saveKey(COMP_KEY, next); setCompForm({ trainingDate: today() });
    say("Competency record saved.");
  };

  const removeComp = (id: string) => { const next = comps.filter(c => c.id !== id); setComps(next); saveKey(COMP_KEY, next); };

  const saveCheck = () => {
    if (!checkForm.drug) { say("Drug name is required for a CD balance check."); return; }
    const rec: CdCheck = { id: `cc-${Date.now()}`, date: checkForm.date || today(), checker: checkForm.checker || "", drug: checkForm.drug || "", balanceExpected: checkForm.balanceExpected || "", balanceActual: checkForm.balanceActual || "", discrepancy: checkForm.balanceExpected !== checkForm.balanceActual, action: checkForm.action || "" };
    const next = [rec, ...checks];
    setChecks(next); saveKey(CHECK_KEY, next); setCheckForm({ date: today(), checker: user && user.name ? user.name : "" });
    say(rec.discrepancy ? "CD DISCREPANCY recorded - investigate and report immediately." : "CD balance check recorded - balanced.");
  };

  const cdBook = load<any>("cd_book");
  const mar = load<any>("mar_entries");
  const meds = load<any>("meds_register");
  const cdNames = new Set(meds.filter((m: any) => m.isCD).map((m: any) => m.medName));

  const cdBookBreaches = cdBook.filter((c: any) => !c.signature2 || !c.balanceAfter);
  const marBreaches = mar.filter((m: any) => m.status === "Given" && cdNames.has(m.medName) && !m.witness);

  const current = comps.filter(c => c.nextDue && daysTo(c.nextDue) >= 0);
  const expired = comps.filter(c => c.nextDue && daysTo(c.nextDue) < 0);
  const noCompetency = comps.filter(c => !c.competencyDate);

  const printMatrix = () => {
    const rows = comps.map(c => `<tr><td style="padding:6px;border-bottom:1px solid #eee">${c.staffName}</td><td style="padding:6px;border-bottom:1px solid #eee">${c.role || "-"}</td><td style="padding:6px;border-bottom:1px solid #eee">${c.trainingDate || "-"}</td><td style="padding:6px;border-bottom:1px solid #eee">${c.competencyDate || "NOT ASSESSED"}</td><td style="padding:6px;border-bottom:1px solid #eee">${c.assessor || "-"}</td><td style="padding:6px;border-bottom:1px solid #eee;color:${c.nextDue && daysTo(c.nextDue) < 0 ? "#dc2626" : "#16a34a"}">${c.nextDue || "-"}</td></tr>`).join("");
    const html = `<!DOCTYPE html><html><head><title>Medication Competency Matrix</title><style>body{font-family:Arial;margin:40px;color:#1f2937}h2{color:#123B36}table{width:100%;border-collapse:collapse;font-size:13px}</style></head><body><h2>Medication Competency Matrix - Roop Cottage</h2><p>Generated ${today()} | Current: ${current.length} | Expired: ${expired.length} | Not yet competency-assessed: ${noCompetency.length}</p><table><tr><th>Staff</th><th>Role</th><th>Training date</th><th>Competency assessed</th><th>Assessor</th><th>Next due</th></tr>${rows}</table><p style="margin-top:16px;font-size:11px;color:#6b7280">Only staff with a current, witnessed competency assessment may administer medicines. Directly addresses the CQC medication finding.</p></body></html>`;
    const win = window.open("", "_blank");
    if (win) { win.document.write(html); win.document.close(); win.focus(); win.print(); }
  };

  return (
    <div className="space-y-6 p-6">
      <div>
        <h2 className="text-xl font-semibold text-slate-900">Medication Competency & CD Witnessing</h2>
        <p className="text-sm text-slate-600">Every administrator competency-tracked, every CD entry witnessed and balanced - zero breaches, fully evidenced.</p>
      </div>

      <div className="grid gap-3 md:grid-cols-4">
        <div className="rounded-xl border border-green-300 bg-green-50 p-4 text-center"><div className="text-2xl font-bold text-green-700">{current.length}</div><div className="text-xs text-green-700">Competencies current</div></div>
        <div className={`rounded-xl border p-4 text-center ${expired.length ? "border-red-300 bg-red-50" : "border-slate-200 bg-white"}`}><div className={`text-2xl font-bold ${expired.length ? "text-red-700" : "text-slate-900"}`}>{expired.length}</div><div className={`text-xs ${expired.length ? "text-red-700" : "text-slate-500"}`}>Expired - do not administer</div></div>
        <div className={`rounded-xl border p-4 text-center ${cdBookBreaches.length + marBreaches.length ? "border-red-300 bg-red-50" : "border-green-300 bg-green-50"}`}><div className={`text-2xl font-bold ${cdBookBreaches.length + marBreaches.length ? "text-red-700" : "text-green-700"}`}>{cdBookBreaches.length + marBreaches.length}</div><div className={`text-xs ${cdBookBreaches.length + marBreaches.length ? "text-red-700" : "text-green-700"}`}>CD witnessing breaches</div></div>
        <div className="rounded-xl border border-slate-200 bg-white p-4 text-center shadow-sm"><div className="text-2xl font-bold text-slate-900">{checks.filter(c => c.discrepancy).length}</div><div className="text-xs text-slate-500">CD discrepancies</div></div>
      </div>

      {message ? <div className="rounded-lg bg-blue-50 p-3 text-sm text-blue-900">{message}</div> : null}

      <div className="flex flex-wrap gap-2">
        {(["comp", "checks", "breaches"] as const).map(t => (
          <button key={t} onClick={() => setTab(t)} className={tab === t ? "rounded-lg bg-blue-900 px-3 py-2 text-sm font-medium text-white" : "rounded-lg border border-slate-300 px-3 py-2 text-sm"}>{t === "comp" ? "Competency Matrix" : t === "checks" ? "CD Balance Checks" : "Witnessing Breaches"}</button>
        ))}
      </div>

      {tab === "comp" ? (
        <div className="space-y-4">
          <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
            <h3 className="mb-3 font-semibold text-slate-900">Record a competency assessment</h3>
            <div className="grid gap-3 md:grid-cols-3">
              <input value={compForm.staffName || ""} onChange={e => setCompForm(f => ({ ...f, staffName: e.target.value }))} placeholder="Staff member" className="rounded-lg border border-slate-300 px-2 py-1 text-sm" />
              <input value={compForm.role || ""} onChange={e => setCompForm(f => ({ ...f, role: e.target.value }))} placeholder="Role" className="rounded-lg border border-slate-300 px-2 py-1 text-sm" />
              <input type="date" value={compForm.trainingDate || ""} onChange={e => setCompForm(f => ({ ...f, trainingDate: e.target.value }))} className="rounded-lg border border-slate-300 px-2 py-1 text-sm" title="Medication training date" />
              <input type="date" value={compForm.competencyDate || ""} onChange={e => setCompForm(f => ({ ...f, competencyDate: e.target.value }))} className="rounded-lg border border-slate-300 px-2 py-1 text-sm" title="Witnessed competency assessment date" />
              <input value={compForm.assessor || ""} onChange={e => setCompForm(f => ({ ...f, assessor: e.target.value }))} placeholder="Assessor (RN / manager)" className="rounded-lg border border-slate-300 px-2 py-1 text-sm" />
              <input type="date" value={compForm.nextDue || ""} onChange={e => setCompForm(f => ({ ...f, nextDue: e.target.value }))} className="rounded-lg border border-slate-300 px-2 py-1 text-sm" title="Next competency due" />
            </div>
            <div className="mt-3 flex gap-2">
              <button onClick={saveComp} className="rounded-lg bg-green-700 px-4 py-2 text-sm font-medium text-white">Save competency</button>
              <button onClick={printMatrix} className="rounded-lg border border-slate-300 px-4 py-2 text-sm">Print matrix</button>
            </div>
          </div>
          <div className="space-y-2">
            {comps.map(c => (
              <div key={c.id} className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-slate-200 bg-white p-3 shadow-sm">
                <div className="text-sm font-medium text-slate-900">{c.staffName} <span className="text-xs text-slate-500">{c.role} | trained {c.trainingDate || "-"} | competency {c.competencyDate || "NOT ASSESSED"} by {c.assessor || "-"}</span></div>
                <div className="flex items-center gap-2">
                  <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${!c.competencyDate ? "bg-red-100 text-red-700" : c.nextDue && daysTo(c.nextDue) < 0 ? "bg-red-100 text-red-700" : c.nextDue && daysTo(c.nextDue) <= 30 ? "bg-amber-100 text-amber-700" : "bg-green-100 text-green-700"}`}>{!c.competencyDate ? "Not assessed - cannot administer" : c.nextDue && daysTo(c.nextDue) < 0 ? "EXPIRED" : c.nextDue ? `Due in ${daysTo(c.nextDue)}d` : "No due date"}</span>
                  <button onClick={() => removeComp(c.id)} className="text-xs text-red-600">Remove</button>
                </div>
              </div>
            ))}
            {comps.length === 0 ? <div className="rounded-lg bg-slate-50 p-4 text-sm text-slate-500">No competency records yet. Record every staff member who administers medicines.</div> : null}
          </div>
        </div>
      ) : null}

      {tab === "checks" ? (
        <div className="space-y-4">
          <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
            <h3 className="mb-3 font-semibold text-slate-900">Record a CD balance check (daily)</h3>
            <div className="grid gap-3 md:grid-cols-4">
              <input type="date" value={checkForm.date || ""} onChange={e => setCheckForm(f => ({ ...f, date: e.target.value }))} className="rounded-lg border border-slate-300 px-2 py-1 text-sm" />
              <input value={checkForm.checker || ""} onChange={e => setCheckForm(f => ({ ...f, checker: e.target.value }))} placeholder="Checker" className="rounded-lg border border-slate-300 px-2 py-1 text-sm" />
              <input value={checkForm.drug || ""} onChange={e => setCheckForm(f => ({ ...f, drug: e.target.value }))} placeholder="CD name & strength" className="rounded-lg border border-slate-300 px-2 py-1 text-sm" />
              <div className="flex gap-2">
                <input value={checkForm.balanceExpected || ""} onChange={e => setCheckForm(f => ({ ...f, balanceExpected: e.target.value }))} placeholder="Book balance" className="w-full rounded-lg border border-slate-300 px-2 py-1 text-sm" />
                <input value={checkForm.balanceActual || ""} onChange={e => setCheckForm(f => ({ ...f, balanceActual: e.target.value }))} placeholder="Actual count" className="w-full rounded-lg border border-slate-300 px-2 py-1 text-sm" />
              </div>
              <input value={checkForm.action || ""} onChange={e => setCheckForm(f => ({ ...f, action: e.target.value }))} placeholder="Action if discrepancy" className="rounded-lg border border-slate-300 px-2 py-1 text-sm md:col-span-3" />
            </div>
            <button onClick={saveCheck} className="mt-3 rounded-lg bg-green-700 px-4 py-2 text-sm font-medium text-white">Record check</button>
          </div>
          <div className="space-y-2">
            {checks.map(c => (
              <div key={c.id} className={`rounded-xl border p-3 shadow-sm ${c.discrepancy ? "border-red-300 bg-red-50" : "border-slate-200 bg-white"}`}>
                <div className="text-sm text-slate-800">{c.date} | {c.checker} | {c.drug} | book {c.balanceExpected} vs actual {c.balanceActual} | {c.discrepancy ? `DISCREPANCY - ${c.action || "action required"}` : "balanced"}</div>
              </div>
            ))}
            {checks.length === 0 ? <div className="rounded-lg bg-slate-50 p-4 text-sm text-slate-500">No CD balance checks recorded yet - check every CD daily.</div> : null}
          </div>
        </div>
      ) : null}

      {tab === "breaches" ? (
        <div className="space-y-2">
          {cdBookBreaches.map((c: any) => (
            <div key={c.id} className="rounded-xl border-2 border-red-300 bg-red-50 p-3">
              <div className="text-sm font-semibold text-red-900">CD book breach - {c.date} | {c.residentName} | {c.medName}</div>
              <div className="mt-1 text-xs text-red-700">Missing: {!c.signature2 ? "second witness signature " : ""}{!c.balanceAfter ? "running balance" : ""}. Correct immediately and record the action taken.</div>
            </div>
          ))}
          {marBreaches.map((m: any) => (
            <div key={m.id} className="rounded-xl border-2 border-red-300 bg-red-50 p-3">
              <div className="text-sm font-semibold text-red-900">eMAR breach - {m.date} {m.round} | {m.residentName} | {m.medName} given WITHOUT witness</div>
              <div className="mt-1 text-xs text-red-700">Controlled drugs must always be witnessed. Investigate, re-witness where possible, and record the learning.</div>
            </div>
          ))}
          {cdBookBreaches.length + marBreaches.length === 0 ? <div className="rounded-lg bg-green-50 p-4 text-sm text-green-700">No witnessing breaches - every CD entry is properly signed and balanced.</div> : null}
        </div>
      ) : null}
    </div>
  );
}